import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Calendar, Phone, MapPin } from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import CateringForm from "@/components/sections/CateringForm";
import CateringBuilder from "@/components/sections/CateringBuilder";
import FinalCTA from "@/components/sections/FinalCTA";

import { BUSINESS } from "@/lib/business-info";
import { EVENT_TYPES, SIGNATURES, TRUST_STATS } from "@/lib/catering-data";

const HERO_BG =
    "https://customer-assets.emergentagent.com/job_b8a7810c-24f2-4266-91d4-0b33b2eb0b66/artifacts/so154t3e_4D544B4D-5ED1-4118-B68A-2CABF55656D8%202.PNG";

const scrollTo = (id, type) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (type) {
        window.dispatchEvent(new CustomEvent("set-inquiry-type", { detail: type }));
    }
};

// ---------------- Hero ----------------
function Hero() {
    return (
        <section
            data-testid="catering-hero"
            className="relative min-h-[92svh] md:min-h-[100svh] overflow-hidden bg-charcoal"
            aria-label="Catering hero"
        >
            {/* Full catering photograph — never cropped (contain).
                Anchored to the top on mobile, to the right on desktop so the
                entire display stays visible alongside the headline. */}
            <div
                aria-hidden
                className="absolute inset-0 z-0 bg-contain bg-no-repeat bg-[center_top] lg:bg-[right_center]"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />

            {/* Mobile scrim — darkens the lower half where the text sits */}
            <div
                aria-hidden
                className="lg:hidden absolute inset-0 z-0 bg-gradient-to-t from-charcoal via-charcoal/65 to-charcoal/5"
            />
            {/* Desktop scrim — elegant dark fill on the left for the headline,
                fading out before the photograph on the right */}
            <div
                aria-hidden
                className="hidden lg:block absolute inset-0 z-0 bg-gradient-to-r from-charcoal from-20% via-charcoal/70 via-55% to-transparent"
            />
            <div
                aria-hidden
                className="hidden lg:block absolute inset-0 z-0 bg-gradient-to-t from-charcoal/45 via-transparent to-transparent"
            />
            <div
                aria-hidden
                className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_22%_55%,rgba(212,175,55,0.07),rgba(0,0,0,0)_55%)]"
            />

            <div className="relative z-10 empire-container min-h-[92svh] md:min-h-[100svh] flex flex-col justify-end lg:justify-center pt-40 pb-24 lg:py-0">
                <div className="w-full lg:max-w-[52%]">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="empire-eyebrow"
                    >
                        <span className="inline-block h-px w-8 bg-gold align-middle mr-3" />
                        Catering · Hudson Valley · {BUSINESS.location}
                    </motion.p>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="catering-headline"
                        className="mt-7 font-serif text-cream text-[2.5rem] leading-[1.07] tracking-[-0.01em] md:text-[3.6rem] lg:text-[4rem]"
                    >
                        Bring the flavor of the{" "}
                        <em className="italic font-normal text-gold">Caribbean</em>{" "}
                        to your next event.
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.32 }}
                        className="mt-7 max-w-xl text-cream/80 text-[15px] md:text-[17px] font-light leading-[1.75]"
                    >
                        From weddings and corporate gatherings to private celebrations
                        and family events, Empire Jamaica Fusion delivers authentic
                        Jamaican cuisine with elegant presentation and warm Caribbean
                        hospitality.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                    >
                        <button
                            type="button"
                            onClick={() => scrollTo("catering-inquiry", "catering")}
                            data-testid="catering-hero-book"
                            className="empire-btn-primary"
                        >
                            Book Catering
                            <ArrowRight size={14} />
                        </button>
                        <a
                            href="/menu"
                            data-testid="catering-hero-view-menu"
                            className="empire-btn-secondary"
                        >
                            View Catering Menu
                        </a>
                    </motion.div>

                    {/* Trust strip */}
                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.3, delay: 0.9 }}
                        className="hidden md:flex flex-wrap items-center gap-x-8 gap-y-3 mt-16 text-cream/60 text-[11px] uppercase tracking-[0.28em]"
                    >
                        <li className="flex items-center gap-3">
                            <span className="h-px w-8 bg-cream/30" />
                            Weddings
                        </li>
                        <li>
                            <span className="text-gold">·</span> Corporate Events
                        </li>
                        <li>
                            <span className="text-gold">·</span> Private Parties
                        </li>
                        <li>
                            <span className="text-gold">·</span> Special Occasions
                        </li>
                    </motion.ul>
                </div>
            </div>

            {/* Scroll cue */}
            <div
                aria-hidden
                className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-cream/40 text-[10px] uppercase tracking-[0.32em]"
            >
                <span>Begin</span>
                <span className="block h-10 w-px bg-gradient-to-b from-gold/70 to-transparent" />
            </div>
        </section>
    );
}

// ---------------- Event Types ----------------
function EventTypes() {
    return (
        <section
            id="event-types"
            data-testid="event-types-section"
            className="relative empire-section bg-charcoal-800"
            aria-label="Event types"
        >
            <div className="empire-container">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-14 md:mb-20">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Designed For
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.4rem] md:text-[3.4rem] lg:text-[4rem] leading-[1.05] tracking-[-0.01em]">
                            Every gathering,<br />
                            <span className="italic text-gold">treated like a private dining room.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        Six event archetypes — each with its own service style,
                        capacity, and menu approach. Tell us which fits and
                        we&rsquo;ll build around it.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cream/[0.07]">
                    {EVENT_TYPES.map((ev, i) => (
                        <motion.article
                            key={ev.id}
                            initial={{ opacity: 0, y: 22 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.06,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`event-card-${ev.id}`}
                            className="group relative overflow-hidden bg-charcoal aspect-[4/5]"
                        >
                            <img
                                src={ev.image}
                                alt={`${ev.title} catering by Empire Jamaica Fusion`}
                                loading="lazy"
                                decoding="async"
                                style={{ objectPosition: ev.objectPosition || "center" }}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.07]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/45 to-transparent" />
                            <div className="absolute inset-0 bg-charcoal/15 group-hover:bg-charcoal/0 transition-colors duration-500" />

                            <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-[0.26em] text-gold">
                                    {ev.guestRange} guests
                                </span>
                                <span className="inline-flex h-9 w-9 items-center justify-center border border-cream/25 text-cream/80 transition-colors group-hover:border-gold group-hover:text-gold">
                                    <ArrowUpRight size={14} />
                                </span>
                            </div>

                            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                                <h3 className="font-serif text-cream text-2xl md:text-[1.7rem] tracking-tight leading-tight">
                                    {ev.title}
                                </h3>
                                <p className="mt-3 text-cream/70 text-[13.5px] font-light leading-relaxed max-w-md">
                                    {ev.note}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------------- Signature Catering Favorites ----------------
function Signatures() {
    return (
        <section
            id="signatures"
            data-testid="signatures-section"
            className="relative empire-section bg-charcoal"
            aria-label="Signature catering favorites"
        >
            <div className="empire-container">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-14 md:mb-20">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                The Travel-Well Menu
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.4rem] md:text-[3.4rem] lg:text-[4rem] leading-[1.05] tracking-[-0.01em]">
                            Signature catering<br />
                            <span className="italic text-gold">favorites.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        Hand-picked plates that hold their soul from kitchen to
                        venue — and turn first-time guests into regulars.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 md:gap-y-20">
                    {SIGNATURES.map((dish, i) => (
                        <motion.article
                            key={dish.name}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{
                                duration: 0.8,
                                delay: (i % 3) * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`signature-card-${dish.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="group"
                        >
                            <div className="relative overflow-hidden aspect-[4/5] bg-charcoal-800">
                                <img
                                    src={dish.image}
                                    alt={`${dish.name} — ${dish.note}`}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.06]"
                                />
                                <div className="absolute inset-0 bg-charcoal/15 group-hover:bg-charcoal/0 transition-colors duration-500" />
                                <span className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.28em] text-gold bg-charcoal/55 backdrop-blur-sm px-3 py-1">
                                    {dish.tag}
                                </span>
                            </div>
                            <div className="mt-6 flex items-baseline gap-3">
                                <span className="font-serif italic text-gold/70 text-[14px]">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <h3 className="font-serif text-cream text-[1.6rem] md:text-[1.9rem] tracking-tight leading-tight transition-colors duration-300 group-hover:text-gold">
                                    {dish.name}
                                </h3>
                            </div>
                            <p className="mt-3 text-cream/65 text-[13.5px] md:text-[14.5px] font-light leading-[1.7] max-w-sm">
                                {dish.note}
                            </p>
                        </motion.article>
                    ))}
                </div>

                <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <a
                        href="/menu"
                        data-testid="signatures-view-menu"
                        className="empire-btn-secondary"
                    >
                        Explore Full Menu
                    </a>
                    <p className="text-cream/50 text-[13px] font-light max-w-md">
                        Vegan, gluten-free and dietary options available across every
                        package.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ---------------- Trust / Social Proof ----------------
function TrustBlock() {
    return (
        <section
            data-testid="trust-section"
            className="relative bg-charcoal-800"
            aria-label="Trust"
        >
            <div className="empire-container py-20 md:py-28">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-end mb-12 md:mb-16">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Quietly proven
                            </span>
                        </div>
                        <h2
                            data-testid="trust-heading"
                            className="mt-5 font-serif text-cream text-[2.2rem] md:text-[3rem] lg:text-[3.6rem] leading-[1.05] tracking-[-0.01em]"
                        >
                            Trusted for<br />
                            <span className="italic text-gold">memorable moments.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        We&rsquo;ve catered hundreds of weddings, boardrooms, milestone
                        birthdays and intimate dinners across the Hudson Valley — and
                        treated every one like our only one.
                    </p>
                </div>

                <ul className="grid grid-cols-2 md:grid-cols-4 gap-px bg-cream/[0.07]">
                    {TRUST_STATS.map((s, i) => (
                        <motion.li
                            key={s.label}
                            initial={{ opacity: 0, y: 22 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`trust-stat-${i}`}
                            className="bg-charcoal p-8 md:p-10 flex flex-col gap-3 hover:bg-charcoal-700 transition-colors duration-500"
                        >
                            <div className="font-serif text-gold text-[2.4rem] md:text-[3rem] leading-none tracking-tight">
                                {s.value}
                            </div>
                            <div className="font-serif text-cream text-[1.05rem] md:text-[1.15rem] leading-tight tracking-tight">
                                {s.label}
                            </div>
                            <p className="text-cream/50 text-[12.5px] font-light tracking-wide">
                                {s.note}
                            </p>
                        </motion.li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

// ---------------- Quick contact bar (just above the form) ----------------
function ContactBar() {
    return (
        <section
            aria-label="Contact"
            className="bg-charcoal border-y border-cream/[0.08]"
        >
            <div className="empire-container py-10 md:py-12">
                <ul className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                    <li className="flex items-start gap-4">
                        <span className="inline-flex h-9 w-9 items-center justify-center border border-gold/40 text-gold shrink-0">
                            <Phone size={14} />
                        </span>
                        <div>
                            <div className="empire-eyebrow !text-cream/55">Direct line</div>
                            <a
                                href={BUSINESS.phoneLink}
                                data-testid="contact-bar-phone"
                                className="mt-1 block font-serif text-cream text-xl tracking-tight hover:text-gold transition-colors"
                            >
                                {BUSINESS.phoneDisplay}
                            </a>
                        </div>
                    </li>
                    <li className="flex items-start gap-4">
                        <span className="inline-flex h-9 w-9 items-center justify-center border border-gold/40 text-gold shrink-0">
                            <Calendar size={14} />
                        </span>
                        <div>
                            <div className="empire-eyebrow !text-cream/55">Response</div>
                            <p className="mt-1 font-serif text-cream text-xl tracking-tight">
                                Within one business day.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start gap-4">
                        <span className="inline-flex h-9 w-9 items-center justify-center border border-gold/40 text-gold shrink-0">
                            <MapPin size={14} />
                        </span>
                        <div>
                            <div className="empire-eyebrow !text-cream/55">Serving</div>
                            <p className="mt-1 font-serif text-cream text-xl tracking-tight">
                                Hudson Valley · NYC metro on request
                            </p>
                        </div>
                    </li>
                </ul>
            </div>
        </section>
    );
}

// ---------------- Page ----------------
export default function Catering() {
    useEffect(() => {
        document.title = "Catering · Empire Jamaica Fusion · Poughkeepsie, NY";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <>
            <Nav />
            <main data-testid="catering-page">
                <Hero />
                <EventTypes />
                <Signatures />
                <CateringBuilder />
                <TrustBlock />
                <ContactBar />
                <CateringForm />
                <FinalCTA />
            </main>
            <Footer />
            <MobileStickyCTA />
        </>
    );
}
